import Highcharts from 'highcharts';
import highchartsExporting from 'highcharts/modules/exporting';
import React, { Dispatch, SetStateAction, useState } from 'react';
import { PortfolioRatioStuffingProducts } from './PortfolioRatioStuffingProducts';
import { PortfolioRatioStuffingAccount } from './PortfolioRatioStuffingAccount';
import { PortfolioRatioStuffingAssets } from './PortfolioRatioStuffingAssets';
import { AssetTypeKey } from 'components/features/utils/assetType';
import { AssetBundleType } from 'components/features/home/Home';
import PremiumModal from 'components/features/modal/PremiumModal';
import Loading from 'components/commons/loading/Loading';
import {
  isPremium,
  isIosAppNotPremium,
} from 'components/features/utils/userStatus';
import { GetUserAssetResult, GetUserBrokerResultItem } from 'config/api';

highchartsExporting(Highcharts);

// ポートフォリオ

export type PortfolioRatioChartDatum = {
  key: TypeKey;
  ratio: number;
};

export type PortfolioRatioChartProps = {
  data: PortfolioRatioChartDatum[];
};

export type PortfolioRatioStuffingProps = {
  brokersInfo: GetUserBrokerResultItem[];
  UserBrokerUpdateTime: React.ReactElement;
  assetBundleType: AssetBundleType;
  asset: GetUserAssetResult;
  getIsOutOfDate: (arg: { account: string }) => boolean | null;
  toPreview: (element: React.ReactElement) => void;
  backHome: () => void;
};

export type PortfolioRatioStuffingTabProps = {
  asset: GetUserAssetResult;
  selectedType: SelectedChartTypeProps;
  setSelectedType: Dispatch<SetStateAction<SelectedChartTypeProps>>;
};

export type AccountList =
  | 'General'
  | 'Identification'
  | 'Nisa'
  | 'DefinedContributionPension'
  | 'Other';

export type AssetsList =
  | 'JP_EQ'
  | 'DC_EQ'
  | 'EM_EQ'
  | 'JP_FI'
  | 'DC_FI'
  | 'EM_FI'
  | 'JP_REIT'
  | 'G_REIT'
  | 'OTHER';

export type SelectedChartTypeProps = 'products' | 'account' | 'assets';

export type TypeKey = AssetTypeKey | AccountList | AssetsList;

export const goToGrouping = (
  pushRouting: (pathArg: { path: string }, isHistory: boolean) => void
) => (groupId: string) => (): void => {
  pushRouting({ path: `/grouping?id=${groupId}` }, true);
};

export const PortfolioRatioStuffing = ({
  brokersInfo,
  UserBrokerUpdateTime,
  assetBundleType,
  asset,
  getIsOutOfDate,
  toPreview,
  backHome,
}: PortfolioRatioStuffingProps): React.ReactElement => {
  const [selectedType, setSelectedType] = useState<SelectedChartTypeProps>(
    'products'
  );

  if (!isPremium() && selectedType === 'assets')
    return (
      <PremiumModal
        layoutPattern={6}
        onClose={() => window.location.reload()}
      />
    );

  return (
    <>
      {selectedType === 'products' && (
        <PortfolioRatioStuffingProducts
          {...{
            asset,
            brokersInfo,
            UserBrokerUpdateTime,
            assetBundleType,
            toPreview,
            backHome,
            selectedType,
            setSelectedType,
            getIsOutOfDate,
          }}
        />
      )}
      {selectedType === 'account' && (
        <PortfolioRatioStuffingAccount
          {...{
            asset,
            brokersInfo,
            UserBrokerUpdateTime,
            assetBundleType,
            toPreview,
            backHome,
            selectedType,
            setSelectedType,
            getIsOutOfDate,
          }}
        />
      )}
      {selectedType === 'assets' && (
        <PortfolioRatioStuffingAssets
          {...{
            asset,
            brokersInfo,
            UserBrokerUpdateTime,
            assetBundleType,
            toPreview,
            backHome,
            selectedType,
            setSelectedType,
          }}
        />
      )}
    </>
  );
};

export const getRatio_ = (total: number) => (value: number): number => {
  return (value / total) * 100;
};

export const PortfolioLoading = ({
  type,
}: {
  type: SelectedChartTypeProps;
}) => {
  return (
    <>
      <div className="bg-bluegray-600">
        <div className="flex justify-center pt-2 bg-bluegray-600 text-white">
          {isIosAppNotPremium() ? (
            <>
              <div
                className="grid ml-4 w-2/3 text-xs border border-white rounded"
                style={{ gridTemplateColumns: '1fr auto 1fr' }}>
                <div
                  className={`text-center py-1 ${
                    type === 'products' ? 'bg-white text-black' : ''
                  }`}>
                  商品
                </div>
                <div
                  className="my-1 border-r border-bluegray-500"
                  style={{ borderWidth: '1px' }}></div>
                <div
                  className={`text-center py-1 ${
                    type === 'account' ? 'bg-white text-black' : ''
                  }`}>
                  口座
                </div>
              </div>
            </>
          ) : (
            <>
              <div
                className="grid ml-4 w-2/3 text-xs border border-white rounded"
                style={{ gridTemplateColumns: '1fr auto 1fr auto 1fr' }}>
                <div
                  className={`text-center py-1 ${
                    type === 'products' ? 'bg-white text-black' : ''
                  }`}>
                  商品
                </div>
                <div
                  className="my-1 border-r border-bluegray-500"
                  style={{ borderWidth: '1px' }}></div>
                <div
                  className={`text-center py-1 ${
                    type === 'account' ? 'bg-white text-black' : ''
                  }`}>
                  口座
                </div>
                <div
                  className="my-1 border-r border-bluegray-500"
                  style={{ borderWidth: '1px' }}></div>
                <div
                  className={`flex flex-row items-center justify-center text-center py-1 ${
                    type === 'assets' ? 'bg-white text-black' : ''
                  }`}>
                  <div>資産分散</div>
                  {!isPremium() && (
                    <div className="icon-lock ml-1 text-lock"></div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        <Loading
          loading={true}
          circularLoading={true}
          style={{
            paddingTop: '120px',
            paddingBottom: '120px',
          }}
        />
      </div>
      <Loading
        loading={true}
        circularLoading={true}
        style={{
          paddingTop: '60px',
          paddingBottom: '60px',
        }}
      />
    </>
  );
};

export type rawDataList = {
  typeKey: TypeKey;
  val: number | null;
};
